import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import { pct, rp } from "@/pages/GalangDanaPage";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = { title: "", category: "", description: "", image: [], target: "", collected: "", deadline: "" };

export default function GalangDanaTab() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/campaigns").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => {
    setForm(item
      ? { title: item.title, category: item.category, description: item.description, image: item.image ? [item.image] : [], target: item.target || "", collected: item.collected || "", deadline: item.deadline || "" }
      : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title, category: form.category, description: form.description,
      image: form.image[0] || "", target: Number(form.target) || 0, collected: Number(form.collected) || 0, deadline: form.deadline || "",
    };
    try {
      if (dlg === "new") await api.post("/campaigns", payload);
      else await api.put(`/campaigns/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="galang-tab">
      <Button onClick={() => open(null)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 mb-6" data-testid="campaign-add-button">
        <Plus className="h-4 w-4 mr-2" /> {t("common.add")}
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm overflow-hidden" data-testid={`campaign-item-${c.id}`}>
            {c.image && <img src={imgUrl(c.image)} alt={c.title} className="aspect-video w-full object-cover" />}
            <div className="p-4">
              <h4 className="font-semibold text-emerald-950 text-sm leading-snug">{c.title}</h4>
              <div className="h-1.5 rounded-full bg-emerald-100 overflow-hidden mt-3">
                <div className="h-1.5 rounded-full bg-[#D4AF37]" style={{ width: `${pct(c)}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">{rp(c.collected)} / {c.target > 0 ? rp(c.target) : "—"}</p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => open(c)} data-testid={`campaign-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />{t("common.edit")}</Button>
                <Button size="sm" variant="destructive" onClick={() => remove(c.id)} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />{t("common.delete")}</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.galang")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="campaign-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Judul Program</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" data-testid="campaign-form-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Kategori</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Dakwah & Kemanusiaan" className="mt-1.5" data-testid="campaign-form-category" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Batas Waktu ({t("common.optional")})</Label>
                <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1.5" data-testid="campaign-form-deadline" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("galang.target")} (Rp)</Label>
                <Input type="number" min="0" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1.5" data-testid="campaign-form-target" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("galang.terkumpul")} (Rp)</Label>
                <Input type="number" min="0" value={form.collected} onChange={(e) => setForm({ ...form, collected: e.target.value })} className="mt-1.5" data-testid="campaign-form-collected" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={6} className="mt-1.5" data-testid="campaign-form-description" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Gambar Sampul</Label>
              <div className="mt-1.5"><ImagePicker values={form.image} onChange={(image) => setForm({ ...form, image })} max={1} testId="campaign-image-picker" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="campaign-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
